import { useState, useEffect, useRef } from 'react';
import {
  Phone, MessageCircle, Mail, Users, Video, Wrench, StickyNote,
  CheckCircle2, Circle, Trash2, Plus, ChevronDown, ChevronUp, Loader2,
  CalendarClock, AlertCircle, Send, X
} from 'lucide-react';
import { api } from '../services/api';
import EmailTemplateSelector, { VariableToolbar } from './EmailTemplateSelector';

const CANAIS = [
  { value: 'Telefone',           label: 'Telefone',            icon: Phone },
  { value: 'WhatsApp',           label: 'WhatsApp',            icon: MessageCircle },
  { value: 'E-mail',             label: 'E-mail',              icon: Mail },
  { value: 'Reunião Presencial', label: 'Reunião Presencial',  icon: Users },
  { value: 'Videochamada',       label: 'Videochamada',        icon: Video },
  { value: 'Visita Técnica',     label: 'Visita Técnica',      icon: Wrench },
  { value: 'Nota Interna',       label: 'Nota Interna',        icon: StickyNote },
];

const CANAL_COLORS = {
  'Telefone':           'bg-blue-50  text-blue-600  border-blue-200',
  'WhatsApp':           'bg-green-50 text-green-600 border-green-200',
  'E-mail':             'bg-purple-50 text-purple-600 border-purple-200',
  'Reunião Presencial': 'bg-orange-50 text-orange-600 border-orange-200',
  'Videochamada':       'bg-cyan-50  text-cyan-600  border-cyan-200',
  'Visita Técnica':     'bg-yellow-50 text-yellow-600 border-yellow-200',
  'Nota Interna':       'bg-gray-50  text-gray-600  border-gray-200',
};

function CanalIcon({ canal, size = 14 }) {
  const ch = CANAIS.find(c => c.value === canal);
  const Icon = ch?.icon || StickyNote;
  return <Icon size={size} />;
}

function formatRelative(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays} dias atrás`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: diffDays > 365 ? 'numeric' : undefined });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

// ── Formulário de nova interação ─────────────────────────────────────────────
function NewInteractionForm({ lead, esns, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    canal: 'WhatsApp',
    descricao: '',
    esn_id: lead.esn_id || '',
    proxima_acao_data: '',
    proxima_acao_descricao: '',
  });
  const [error, setError] = useState('');

  function handleChange(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    if (error) setError('');
  }

  async function handleSubmit() {
    if (!form.descricao.trim()) { setError('Descrição é obrigatória'); return; }
    if (!form.esn_id) { setError('ESN responsável é obrigatório'); return; }

    setSaving(true);
    try {
      const created = await api.createInteraction(lead.id, {
        canal: form.canal,
        descricao: form.descricao,
        esn_id: Number(form.esn_id),
        proxima_acao_data: form.proxima_acao_data || null,
        proxima_acao_descricao: form.proxima_acao_descricao || null,
      });
      onCreated(created);
      setForm({ canal: 'WhatsApp', descricao: '', esn_id: lead.esn_id || '', proxima_acao_data: '', proxima_acao_descricao: '' });
      setOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border-2 border-dashed border-[#2fc6f6]/50 rounded-lg text-sm text-[#2fc6f6] hover:bg-[#2fc6f6]/5 transition-colors"
      >
        <Plus size={16} /> Registrar interação
      </button>
    );
  }

  return (
    <div className="border border-[#2fc6f6]/30 rounded-lg p-4 bg-[#2fc6f6]/5 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-[#535c69]">Nova Interação</span>
        <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 text-xs">Cancelar</button>
      </div>

      {/* Canal + ESN */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Canal *</label>
          <select
            value={form.canal}
            onChange={e => handleChange('canal', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#2fc6f6]"
          >
            {CANAIS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">ESN responsável *</label>
          <select
            value={form.esn_id}
            onChange={e => handleChange('esn_id', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#2fc6f6]"
          >
            <option value="">Selecione...</option>
            {esns.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
        </div>
      </div>

      {/* Descrição */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">O que aconteceu? *</label>
        <textarea
          rows={3}
          value={form.descricao}
          onChange={e => handleChange('descricao', e.target.value)}
          placeholder="Descreva o contato, o que foi discutido, reações do cliente..."
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#2fc6f6] resize-none"
        />
      </div>

      {/* Próxima ação */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Data da próxima ação</label>
          <input
            type="date"
            value={form.proxima_acao_data}
            onChange={e => handleChange('proxima_acao_data', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#2fc6f6]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">O que fazer?</label>
          <input
            type="text"
            value={form.proxima_acao_descricao}
            onChange={e => handleChange('proxima_acao_descricao', e.target.value)}
            placeholder="Ex: Enviar proposta, Ligar para decisor..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#2fc6f6]"
          />
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-[#2fc6f6] rounded-lg hover:bg-[#2fc6f6]/80 disabled:opacity-50"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          {saving ? 'Salvando...' : 'Registrar'}
        </button>
      </div>
    </div>
  );
}

// ── Card de interação individual ─────────────────────────────────────────────
function InteractionCard({ interaction, onConcluir, onDelete }) {
  const [deleting, setDeleting] = useState(false);
  const [concluding, setConcluding] = useState(false);
  const colorClass = CANAL_COLORS[interaction.canal] || CANAL_COLORS['Nota Interna'];

  const isOverdue = interaction.proxima_acao_data &&
    !interaction.proxima_acao_concluida &&
    interaction.proxima_acao_data < new Date().toISOString().split('T')[0];

  async function handleConcluir() {
    setConcluding(true);
    try { await onConcluir(interaction.id); }
    finally { setConcluding(false); }
  }

  async function handleDelete() {
    if (!confirm('Excluir esta interação?')) return;
    setDeleting(true);
    try { await onDelete(interaction.id); }
    finally { setDeleting(false); }
  }

  return (
    <div className="relative pl-6">
      {/* Linha vertical da timeline */}
      <div className="absolute left-[9px] top-6 bottom-0 w-px bg-gray-200" />
      {/* Ponto da timeline */}
      <div className={`absolute left-0 top-4 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${colorClass}`}>
        <CanalIcon canal={interaction.canal} size={10} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 ml-2">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
              <CanalIcon canal={interaction.canal} size={11} />
              {interaction.canal}
            </span>
            <span className="text-xs text-gray-400">{formatRelative(interaction.data_interacao)}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="p-1 text-gray-300 hover:text-red-400 transition-colors"
              title="Excluir"
            >
              {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            </button>
          </div>
        </div>

        {/* Descrição */}
        <p className="text-sm text-gray-700 leading-relaxed mb-2">{interaction.descricao}</p>

        {/* Rodapé: ESN + próxima ação */}
        <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
          <span>{interaction.esn_nome || '—'}</span>
          {interaction.data_interacao && (
            <span>{new Date(interaction.data_interacao).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
          )}
        </div>

        {/* Próxima ação */}
        {interaction.proxima_acao_descricao && (
          <div className={`mt-2 pt-2 border-t flex items-start gap-2 ${isOverdue ? 'border-red-200' : 'border-gray-100'}`}>
            <button
              type="button"
              onClick={handleConcluir}
              disabled={concluding || interaction.proxima_acao_concluida}
              className={`shrink-0 mt-0.5 ${interaction.proxima_acao_concluida ? 'text-green-500' : isOverdue ? 'text-red-400 hover:text-green-500' : 'text-gray-300 hover:text-green-500'} transition-colors disabled:cursor-default`}
              title={interaction.proxima_acao_concluida ? 'Concluída' : 'Marcar como concluída'}
            >
              {concluding
                ? <Loader2 size={14} className="animate-spin" />
                : interaction.proxima_acao_concluida
                ? <CheckCircle2 size={14} />
                : <Circle size={14} />
              }
            </button>
            <div className="flex-1">
              <p className={`text-xs ${interaction.proxima_acao_concluida ? 'line-through text-gray-400' : isOverdue ? 'text-red-500 font-medium' : 'text-gray-600'}`}>
                {interaction.proxima_acao_descricao}
              </p>
              {interaction.proxima_acao_data && (
                <span className={`inline-flex items-center gap-1 text-xs mt-0.5 ${isOverdue ? 'text-red-500' : 'text-gray-400'}`}>
                  <CalendarClock size={11} />
                  {formatDate(interaction.proxima_acao_data)}
                  {isOverdue && ' — ATRASADA'}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Modal de envio de e-mail ──────────────────────────────────────────────────
function EmailModal({ lead, onClose, onSent }) {
  const [form, setForm] = useState({
    to: lead?.contato_email || '',
    subject: '',
    body_text: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  async function handleSend(e) {
    e.preventDefault();
    if (!form.to.trim() || !form.subject.trim()) {
      setError('Destinatário e assunto são obrigatórios');
      return;
    }
    setSending(true);
    setError('');
    try {
      const interaction = await api.sendEmailInteraction({
        lead_id: lead.id,
        to: form.to,
        subject: form.subject,
        body_text: form.body_text,
      });
      onSent(interaction);
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao enviar e-mail');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-purple-500" />
            <span className="font-semibold text-gray-800">Enviar E-mail</span>
          </div>
          <div className="flex items-center gap-2">
            <EmailTemplateSelector lead={lead} onApply={({ subject, body }) => setForm(f => ({ ...f, subject, body_text: body }))} />
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
          </div>
        </div>

        <form onSubmit={handleSend} className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Para *</label>
            <input
              type="email"
              value={form.to}
              onChange={e => setForm(f => ({ ...f, to: e.target.value }))}
              placeholder="email@empresa.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/40"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Assunto *</label>
            <input
              type="text"
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              placeholder="Ex: Proposta TOTVS Moda — Referência ao lead"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400/40"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-600">Mensagem</label>
              <VariableToolbar
                textareaRef={bodyRef}
                value={form.body_text}
                onChange={val => setForm(f => ({ ...f, body_text: val }))}
              />
            </div>
            <textarea
              ref={bodyRef}
              rows={6}
              value={form.body_text}
              onChange={e => setForm(f => ({ ...f, body_text: e.target.value }))}
              placeholder="Corpo do e-mail..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-400/40"
            />
          </div>

          {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={sending}
              className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function InteractionsTab({ lead, esns }) {
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const toastTimerRef = useRef(null);

  useEffect(() => {
    if (!lead?.id) return;
    setInteractions([]); // clear immediately — prevents flash of previous lead's data
    setLoading(true);
    api.getInteractions(lead.id)
      .then(setInteractions)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [lead?.id]);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  function showToast(msg) {
    setToastMsg(msg);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(''), 3000);
  }

  function handleCreated(interaction) {
    setInteractions(prev => [interaction, ...prev]);
    showToast('Interação registrada ✓');
  }

  async function handleConcluir(interactionId) {
    await api.concluirInteraction(lead.id, interactionId);
    setInteractions(prev =>
      prev.map(i => i.id === interactionId ? { ...i, proxima_acao_concluida: 1 } : i)
    );
  }

  async function handleDelete(interactionId) {
    await api.deleteInteraction(lead.id, interactionId);
    setInteractions(prev => prev.filter(i => i.id !== interactionId));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-[#2fc6f6]" />
      </div>
    );
  }

  function handleEmailSent(interaction) {
    setInteractions(prev => [interaction, ...prev]);
    showToast('E-mail registrado na timeline ✓');
  }

  return (
    <div className="space-y-4">
      {toastMsg && (
        <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 size={14} />
          {toastMsg}
        </div>
      )}
      <div className="space-y-2">
        <NewInteractionForm lead={lead} esns={esns} onCreated={handleCreated} />
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Enviar mensagem:</span>
          <button
            type="button"
            onClick={() => setEmailModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-purple-600 border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors"
            title="Enviar e-mail ao contato"
          >
            <Mail size={14} />
            E-mail
          </button>
        </div>
      </div>

      {emailModalOpen && (
        <EmailModal
          lead={lead}
          onClose={() => setEmailModalOpen(false)}
          onSent={handleEmailSent}
        />
      )}

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 rounded-lg p-3">
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {interactions.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">Nenhuma interação registrada ainda.</p>
          <p className="text-xs mt-1">Registre o primeiro contato acima.</p>
        </div>
      ) : (
        <div className="relative">
          {interactions.map(interaction => (
            <InteractionCard
              key={interaction.id}
              interaction={interaction}
              leadId={lead.id}
              onConcluir={handleConcluir}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
